import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import ru from '../locales/ru';
import kz from '../locales/kz';
import en from '../locales/en';

const LanguageContext = createContext();

const translations = { ru, kk: kz, en };

export const LanguageProvider = ({ children }) => {
    const [guestLang, setGuestLang] = useState(localStorage.getItem('app_lang') || 'ru');
    const [adminLang, setAdminLang] = useState(localStorage.getItem('admin_lang') || 'ru');
    const [waiterLang, setWaiterLang] = useState(localStorage.getItem('waiter_lang') || 'ru');
    const [availableLanguages, setAvailableLanguages] = useState(['ru', 'kk', 'en']);

    useEffect(() => {
        localStorage.setItem('app_lang', guestLang);
    }, [guestLang]);

    useEffect(() => {
        localStorage.setItem('admin_lang', adminLang);
    }, [adminLang]);

    useEffect(() => {
        localStorage.setItem('waiter_lang', waiterLang);
    }, [waiterLang]);

    const translate = useCallback((key, currentLang, fallback) => {
        return translations[currentLang]?.[key] || translations['ru']?.[key] || fallback || key;
    }, []);

    const translateData = useCallback((originalText, dataTranslations, field, currentLang) => {
        // 1. Try DB translation
        // Special case for Kazakh: handle both 'kk' and 'kz' keys
        let dbTrans;
        if (currentLang === 'kk') {
            dbTrans = dataTranslations?.['kk']?.[field] || dataTranslations?.['kk'] || 
                      dataTranslations?.['kz']?.[field] || dataTranslations?.['kz'];
        } else {
            dbTrans = dataTranslations?.[currentLang]?.[field] || dataTranslations?.[currentLang];
        }
        
        if (dbTrans && typeof dbTrans === 'string' && dbTrans.trim()) return dbTrans;

        // 2. Try Dictionary fallback (if text exists and not RU)
        if (originalText && currentLang !== 'ru') {
            const sourceDict = translations['ru'];
            const targetDict = translations[currentLang];

            if (sourceDict && targetDict) {
                const entry = Object.entries(sourceDict).find(([key, val]) =>
                    val && val.toString().toLowerCase() === originalText.toString().toLowerCase()
                );

                if (entry && targetDict[entry[0]]) {
                    return targetDict[entry[0]];
                }
            }
        }

        // 3. Return original text if valid
        if (originalText && typeof originalText === 'string' && originalText.trim()) {
            return originalText;
        }

        // 4. Fallback to ANY available translation (if base text is missing)
        if (dataTranslations) {
            for (const langKey of Object.keys(dataTranslations)) {
                const val = dataTranslations[langKey]?.[field] || dataTranslations[langKey];
                if (val && typeof val === 'string' && val.trim()) return val;
            }
        }

        return '';
    }, []);

    return (
        <LanguageContext.Provider value={{
            guestLang, setGuestLang,
            adminLang, setAdminLang,
            waiterLang, setWaiterLang,
            availableLanguages, setAvailableLanguages,
            translate,
            translateData
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const { guestLang, setGuestLang, availableLanguages, setAvailableLanguages, translate, translateData } = useContext(LanguageContext);

    const t = useCallback((key, fallback) => translate(key, guestLang, fallback), [translate, guestLang]);
    const tData = useCallback((text, trans, field = 'name') => translateData(text, trans, field, guestLang), [translateData, guestLang]);

    return {
        lang: guestLang,
        setLang: setGuestLang,
        availableLanguages,
        setAvailableLanguages,
        t,
        tData
    };
};

export const useAdminLanguage = () => {
    const { adminLang, setAdminLang, availableLanguages, setAvailableLanguages, translate, translateData } = useContext(LanguageContext);

    const t = useCallback((key, fallback) => translate(key, adminLang, fallback), [translate, adminLang]);
    const tData = useCallback((text, trans, field = 'name') => translateData(text, trans, field, adminLang), [translateData, adminLang]);

    return {
        lang: adminLang,
        setLang: setAdminLang,
        availableLanguages,
        setAvailableLanguages,
        t,
        tData
    };
};

export const useWaiterLanguage = () => {
    const { waiterLang, setWaiterLang, translate, translateData } = useContext(LanguageContext);

    const t = useCallback((key, fallback) => translate(key, waiterLang, fallback), [translate, waiterLang]);
    const tData = useCallback((text, trans, field = 'name') => translateData(text, trans, field, waiterLang), [translateData, waiterLang]);

    return {
        lang: waiterLang,
        setLang: setWaiterLang,
        t,
        tData
    };
};
